import React, { useState } from 'react';
import axios from 'axios';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Send to backend API
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });
      
      setIsSubmitted(true);
      
      // Reset after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 5000);
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const googleMapsUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2730.8!2d6.933!3d46.859!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478e6ae1f8e5b6ed%3A0x1!2sChemin+des+Vergers+15%2C+1562+Corcelles-pr%C3%A8s-Payerne!5e0!3m2!1sfr!2sch!4v1700000000000!5m2!1sfr!2sch";

  return (
    <section id="contact" className="bg-gray-50 py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Title */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">
            Contact
          </h2>
          <div className="w-12 h-0.5 bg-gray-300"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <div>
            {isSubmitted ? (
              <div className="bg-white border border-gray-200 p-8 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-900 font-medium mb-2">Message envoyé</p>
                <p className="text-gray-600 text-sm">Nous vous recontacterons dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                    {error}
                  </div>
                )}
                
                <div>
                  <label htmlFor="name" className="block text-gray-700 text-sm font-medium mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:border-gray-500 transition-colors"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:border-gray-500 transition-colors"
                    placeholder="votre@email.ch"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-gray-700 text-sm font-medium mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:border-gray-500 transition-colors"
                    placeholder="+41 XX XXX XX XX"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 text-sm font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-white border border-gray-300 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:border-gray-500 transition-colors resize-none"
                    placeholder="Décrivez votre projet..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gray-900 text-white font-medium text-sm px-8 py-3 rounded hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                </button>
              </form>
            )}
          </div>

          {/* Contact Info & Map */}
          <div className="space-y-10">
            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Adresse</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {companyInfo.address.street}<br />
                  {companyInfo.address.postalCode} {companyInfo.address.city}<br />
                  {companyInfo.address.country}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Téléphone</h4>
                <div className="space-y-2">
                  {companyInfo.phone.map((contact, index) => (
                    <div key={index} className="text-sm">
                      <a 
                        href={`tel:${contact.number.replace(/\s/g, '')}`}
                        className="text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        {contact.number}
                      </a>
                      <span className="text-gray-500 ml-2">
                        {contact.name} – {contact.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Email</h4>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-600 text-sm hover:text-gray-900 transition-colors"
                >
                  {companyInfo.email}
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="aspect-[4/3] bg-gray-100">
              <iframe
                src={googleMapsUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation Metaj Construction"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
